import React, { useEffect, useMemo, useState } from 'react';
import axiosInstance from '../../utils/axiosInstance';

const PRINT_STYLES = `
@media print {
  @page { size: A4 landscape; margin: 14mm 12mm; }
  .print-hide { display: none !important; }
  body { background: #fff !important; }
  #classroom-utilization-print table { page-break-inside: auto; }
  #classroom-utilization-print tr { page-break-inside: avoid; break-inside: avoid; }
  #classroom-utilization-print .print-bucket:not(:first-child) { page-break-before: always; break-before: page; }
  #classroom-utilization-print .print-signoff-group { page-break-inside: avoid; break-inside: avoid; }
}
`;

function utilBadgeStyle(pct) {
  if (pct === null || pct === undefined) return { background: '#f3f4f6', color: '#6b7280' };
  if (pct >= 70) return { background: '#dcfce7', color: '#15803d' };
  if (pct >= 40) return { background: '#fef3c7', color: '#b45309' };
  return { background: '#fee2e2', color: '#b91c1c' };
}

function UtilizationTable({ rooms }) {
  return (
    <table className="w-full border-collapse text-xs">
      <thead>
        <tr className="border border-gray-400 bg-gray-100">
          <th className="border border-gray-400 px-2 py-1 text-left">Room</th>
          <th className="border border-gray-400 px-2 py-1 text-left">Floor</th>
          <th className="border border-gray-400 px-2 py-1 text-center">Sessions</th>
          <th className="border border-gray-400 px-2 py-1 text-center">Class (h)</th>
          <th className="border border-gray-400 px-2 py-1 text-center">Event (h)</th>
          <th className="border border-gray-400 px-2 py-1 text-center">Booked (h)</th>
          <th className="border border-gray-400 px-2 py-1 text-center">Blocked (h)</th>
          <th className="border border-gray-400 px-2 py-1 text-center">Available (h)</th>
          <th className="border border-gray-400 px-2 py-1 text-center">Utilization</th>
        </tr>
      </thead>
      <tbody>
        {rooms.map((r) => (
          <tr key={r.classroomId}>
            <td className="border border-gray-400 px-2 py-1 font-medium">{r.name}</td>
            <td className="border border-gray-400 px-2 py-1">{r.floor}</td>
            <td className="border border-gray-400 px-2 py-1 text-center">{r.sessionCount}</td>
            <td className="border border-gray-400 px-2 py-1 text-center">{r.classHours}</td>
            <td className="border border-gray-400 px-2 py-1 text-center">{r.eventHours}</td>
            <td className="border border-gray-400 px-2 py-1 text-center font-semibold">{r.bookedHours}</td>
            <td className="border border-gray-400 px-2 py-1 text-center">{r.blockedHours}</td>
            <td className="border border-gray-400 px-2 py-1 text-center">{r.availableHours}</td>
            <td className="border border-gray-400 px-2 py-1 text-center font-semibold" style={utilBadgeStyle(r.utilizationPct)}>
              {r.utilizationPct === null ? '—' : `${r.utilizationPct}%`}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function BucketSection({ title, bucket, dayCount, operatingHoursPerDay }) {
  if (!bucket) return null;
  const { rooms, summary } = bucket;

  return (
    <div className="print-bucket">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-base font-bold text-gray-900">{title}</h3>
        <span className="text-[11px] text-gray-500">
          {dayCount} day{dayCount === 1 ? '' : 's'} in range &middot; {operatingHoursPerDay}h/day assumed operating hours
        </span>
      </div>

      {summary && (
        <div className="mb-3 grid grid-cols-4 gap-3 text-xs">
          <div className="border border-gray-300 p-2 text-center">
            <div className="text-base font-bold text-gray-900">{summary.avgUtilizationPct ?? '—'}%</div>
            <div className="text-[10px] text-gray-500">Average utilization</div>
          </div>
          <div className="border border-gray-300 p-2 text-center">
            <div className="text-base font-bold text-gray-900">{summary.totalBookedHours}h</div>
            <div className="text-[10px] text-gray-500">
              Total booked (Class {summary.totalClassHours}h &middot; Event {summary.totalEventHours}h)
            </div>
          </div>
          <div className="border border-gray-300 p-2 text-center">
            <div className="truncate text-sm font-bold text-emerald-700">{summary.mostUtilized?.name || '—'}</div>
            <div className="text-[10px] text-gray-500">Most utilized ({summary.mostUtilized?.utilizationPct ?? '—'}%)</div>
          </div>
          <div className="border border-gray-300 p-2 text-center">
            <div className="truncate text-sm font-bold text-red-700">{summary.leastUtilized?.name || '—'}</div>
            <div className="text-[10px] text-gray-500">Least utilized ({summary.leastUtilized?.utilizationPct ?? '—'}%)</div>
          </div>
        </div>
      )}

      {rooms.length === 0 ? (
        <p className="text-center text-sm text-gray-500">No classrooms found.</p>
      ) : (
        <UtilizationTable rooms={rooms} />
      )}
    </div>
  );
}

export default function ClassroomUtilizationPrintView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);

  const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const startDate = searchParams.get('startDate');
  const endDate = searchParams.get('endDate');
  const operatingHoursPerDayParam = searchParams.get('operatingHoursPerDay') || '8.5';

  useEffect(() => {
    let isMounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await axiosInstance.get('/admin/dashboard/classroom-utilization', {
          params: { startDate, endDate, operatingHoursPerDay: operatingHoursPerDayParam },
        });
        if (!isMounted) return;
        setData(resp?.data || null);
        setGeneratedAt(new Date().toISOString());
      } catch (err) {
        if (isMounted) setError(err?.response?.data?.message || 'Failed to load classroom utilization report');
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-open the print dialog once the document has rendered
  useEffect(() => {
    if (loading || error) return;
    const timer = setTimeout(() => window.print(), 400);
    return () => clearTimeout(timer);
  }, [loading, error]);

  return (
    <>
      <style>{PRINT_STYLES}</style>
      <div className="min-h-screen bg-gray-100">
        <div className="print-hide sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-3 shadow-sm">
          <span className="text-sm font-medium text-gray-600">Print preview — Classroom Utilization Report</span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => window.print()}
              className="rounded-lg border border-indigo-600 bg-indigo-600 px-4 py-2 text-xs font-medium text-white hover:bg-indigo-700"
            >
              Print
            </button>
            <button
              type="button"
              onClick={() => window.close()}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>

        <div id="classroom-utilization-print" className="mx-auto max-w-5xl bg-white p-10 shadow-sm print:shadow-none">
          {loading ? (
            <p className="text-center text-gray-500">Loading…</p>
          ) : error ? (
            <p className="text-center text-red-600">{error}</p>
          ) : !data ? (
            <p className="text-center text-gray-500">No data available.</p>
          ) : (
            <>
              {/* Letterhead */}
              <div className="border-b-2 border-gray-800 pb-4 text-center">
                <h1 className="text-2xl font-black tracking-wide text-gray-900">ESOFT METRO CAMPUS BATTICALOA</h1>
                <p className="mt-0.5 text-xs uppercase tracking-widest text-gray-500">Academic Affairs Division</p>
              </div>

              <div className="mt-5 text-center">
                <span className="inline-block rounded-sm border border-gray-800 px-4 py-1 text-xs font-bold uppercase tracking-widest text-gray-800">
                  Official Report
                </span>
                <h2 className="mt-3 text-xl font-bold text-gray-900">Classroom Utilization Report</h2>
                <p className="mt-1 text-sm text-gray-600">Resource Allocation &amp; Capacity Planning Summary</p>
              </div>

              <div className="mt-5 grid grid-cols-2 gap-x-6 gap-y-1 border border-gray-300 px-4 py-3 text-xs text-gray-700">
                <div><span className="font-semibold">Period:</span> {data.startDate} to {data.endDate}</div>
                <div><span className="font-semibold">Generated:</span> {generatedAt ? new Date(generatedAt).toLocaleString('en-GB') : '—'}</div>
                <div><span className="font-semibold">Operating hours/day (assumed):</span> {data.operatingHoursPerDay}h</div>
                <div><span className="font-semibold">Days in range:</span> {data.weekdayDayCount} weekday, {data.weekendDayCount} weekend</div>
              </div>

              <div className="mt-8 space-y-8">
                <BucketSection
                  title="Weekday Utilization"
                  bucket={data.weekday}
                  dayCount={data.weekdayDayCount}
                  operatingHoursPerDay={data.operatingHoursPerDay}
                />

                {/* Last section grouped with the sign-off block so the signature
                    never lands alone at the top of a fresh page. */}
                <div className="print-signoff-group space-y-5">
                  <BucketSection
                    title="Weekend Utilization"
                    bucket={data.weekend}
                    dayCount={data.weekendDayCount}
                    operatingHoursPerDay={data.operatingHoursPerDay}
                  />

                  <div className="mt-8 grid grid-cols-2 gap-10 text-xs text-gray-600">
                    <div>
                      <div className="h-16"></div>
                      <div className="border-t border-gray-800 pt-1">Prepared by — Signature &amp; Date</div>
                    </div>
                    <div>
                      <div className="h-16"></div>
                      <div className="border-t border-gray-800 pt-1">Approved by (CEO) — Signature &amp; Date</div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
